/**
 * useGroupCustomCode
 *
 * Client-only composable that watches `currentGroup.settings.customCode` and
 * injects/removes the group's custom CSS, JS, header tracking code, and footer
 * tracking code into the live DOM.
 *
 * Mirrors the logic in master/apps/communities-preview/src/helper/customCode.helper.ts
 * (`initiateCustomTagsCommunities`) but ported to the staging Vue 3 / Nuxt 3 layer
 * architecture.
 *
 * Injection happens when `liveMode === true` or when the URL query `customCode`
 * is `'true'` (test preview tab). `?customCode=false` suppresses injection even
 * in live mode.
 *
 * All injected elements are tagged with a `data-group-custom-code` attribute so
 * they can be cleanly removed when the group changes or the component unmounts.
 * Head `<style>` / `<script>` for customCss/customJs also use stable ids
 * (`GROUP_CUSTOM_CODE_INJECTED_STYLE_ID`, `GROUP_CUSTOM_CODE_INJECTED_SCRIPT_ID`).
 */

import { computed, onMounted, onUnmounted, watch } from 'vue'

import { useRoute } from 'nuxt/app'

import { useGroups } from './useGroups'
import type { IGroupCustomCode } from '../types/types'

const ATTR = 'data-group-custom-code'

/** Stable head element ids for injected CSS/JS (tests and deterministic DOM queries). */
export const GROUP_CUSTOM_CODE_INJECTED_STYLE_ID = 'group-custom-code-injected-css'
export const GROUP_CUSTOM_CODE_INJECTED_SCRIPT_ID = 'group-custom-code-injected-js'

/** Normalise `route.query.customCode` (string, string[], or boolean from bad URLs). */
export function normalizePreviewQueryParam(value: unknown): '' | 'true' | 'false' {
  if (value === true || value === 'true') return 'true'
  if (value === false || value === 'false') return 'false'
  if (Array.isArray(value)) return normalizePreviewQueryParam(value[0])
  return ''
}

/** Whether stored group custom code should be injected for this route flag. */
export function shouldInjectGroupCustomCode(code: IGroupCustomCode, routeQueryCustomCode: unknown): boolean {
  if (normalizePreviewQueryParam(routeQueryCustomCode) === 'false') return false
  if (code.liveMode === true) return true
  return normalizePreviewQueryParam(routeQueryCustomCode) === 'true'
}

/**
 * True when the URL is group-scoped (communities group slug in path or `groupSlug` param).
 * Off these routes we must not apply `currentGroup.settings.customCode` so a stale store
 * does not leak into courses, account settings, etc.
 */
export function isActiveGroupCustomCodeRoute(route: { path: string; params: Record<string, unknown> }): boolean {
  const slug = route.params?.groupSlug
  if (typeof slug === 'string' && slug.trim().length > 0) {
    return true
  }
  const match = route.path.match(/^\/communities\/groups\/([^/]+)/)
  return Boolean(match?.[1]?.length)
}

/** Serialise a customCode object to a stable string for content-equality checks. */
function serializeCustomCode(code: IGroupCustomCode): string {
  return JSON.stringify({
    customJs: code.customJs ?? '',
    customCss: code.customCss ?? '',
    headerCode: code.headerCode ?? '',
    footerCode: code.footerCode ?? '',
    liveMode: code.liveMode ?? false,
  })
}

/** Dedupe key: preview query + payload so `?customCode=true` toggles re-apply. */
function serializeApplyInputs(code: IGroupCustomCode | undefined, routeQueryCustomCode: unknown): string {
  const q = normalizePreviewQueryParam(routeQueryCustomCode)
  if (!code) return `null|${q}`
  return `${q}|${serializeCustomCode(code)}`
}

/**
 * Wrap raw JS in a try/catch so errors in customer code don't propagate to
 * our own error monitoring.  Mirrors `sanitizeJavaScriptCode` from master.
 */
function sanitizeJs(jsCode: string): string {
  return `try {
  ${jsCode.replace(/<\/?[^>]+(>|$)/g, '').trim()}
} catch (error) {
  console.error('Custom JS execution error:', error);
}`
}

/** Remove all previously injected custom-code elements. */
function removeInjectedElements(): void {
  document.querySelectorAll(`[${ATTR}]`).forEach(el => el.remove())
}

function injectCss(css: string): void {
  if (!css) return
  const style = document.createElement('style')
  style.id = GROUP_CUSTOM_CODE_INJECTED_STYLE_ID
  style.setAttribute('type', 'text/css')
  style.setAttribute(ATTR, 'css')
  style.innerHTML = css
  document.head.appendChild(style)
}

function injectJs(js: string): void {
  if (!js) return
  const sanitized = sanitizeJs(js)

  // Validate before injecting
  try {
    new Function(sanitized)
  } catch (e) {
    console.error('Custom JS is invalid — skipping injection:', e)
    return
  }

  const script = document.createElement('script')
  script.id = GROUP_CUSTOM_CODE_INJECTED_SCRIPT_ID
  script.setAttribute('type', 'text/javascript')
  script.setAttribute(ATTR, 'js')
  script.innerHTML = sanitized
  document.head.appendChild(script)
}

function injectHtml(targetId: string, html: string, attrValue: string): void {
  if (!html) return
  const target = document.getElementById(targetId)
  if (!target) return

  // If the value has no HTML tags, treat it as raw JavaScript and wrap it.
  // This handles cases where users enter plain JS instead of HTML with <script> tags.
  const normalized = html.includes('<') ? html : `<script>${html}<\/script>`

  // Parse the HTML string and re-create elements so browser executes scripts.
  // DOMParser-parsed <script> nodes are inert — they must be re-created to execute.
  //
  // IMPORTANT: The HTML5 parser places bare <script> tags (with no preceding body
  // content) into <head>, not <body>. Walk both to handle all cases.
  const parser = new DOMParser()
  const doc = parser.parseFromString(normalized, 'text/html')

  const allNodes = [...Array.from(doc.head.childNodes), ...Array.from(doc.body.childNodes)]

  allNodes.forEach(node => {
    if (node.nodeName === 'SCRIPT') {
      const original = node as HTMLScriptElement
      const script = document.createElement('script')
      script.setAttribute(ATTR, attrValue)
      if (original.src) {
        script.src = original.src
        script.async = original.async
      } else {
        script.innerHTML = original.innerHTML
      }
      Array.from(original.attributes).forEach(attr => {
        if (attr.name !== 'src') script.setAttribute(attr.name, attr.value)
      })
      target.appendChild(script)
    } else {
      const clone = (node as Element).cloneNode?.(true) as Element
      if (clone && clone.setAttribute) clone.setAttribute(ATTR, attrValue)
      if (clone) target.appendChild(clone)
    }
  })
}

function applyCustomCode(code: IGroupCustomCode, routeQueryCustomCode: unknown): void {
  removeInjectedElements()

  if (!shouldInjectGroupCustomCode(code, routeQueryCustomCode)) return

  injectCss(code.customCss ?? '')
  injectJs(code.customJs ?? '')
  injectHtml('communities-siteCustomHeader', code.headerCode ?? '', 'header')
  injectHtml('communities-siteCustomFooter', code.footerCode ?? '', 'footer')
}

/**
 * Call this composable once in the root layout/container component.
 * It watches `currentGroup` from the groups store and re-applies custom code
 * whenever the group changes.
 */
export function useGroupCustomCode(): void {
  if (process.server) return

  const route = useRoute()
  const { store: groupStore } = useGroups()

  // Track the last applied content so we don't re-inject when the group object
  // is replaced with a new reference carrying identical customCode (e.g. after
  // SSR hydration + TanStack Query client-side refetch).
  let lastAppliedContent: string | null = null

  const effectiveCustomCode = computed(() => {
    if (!isActiveGroupCustomCodeRoute(route)) {
      return undefined
    }
    return groupStore.currentGroup?.settings?.customCode
  })

  function applyIfChanged(customCode: IGroupCustomCode | undefined): void {
    const queryFlag = route.query.customCode
    const content = serializeApplyInputs(customCode, queryFlag)
    if (content === lastAppliedContent) return
    lastAppliedContent = content
    if (customCode) {
      applyCustomCode(customCode, queryFlag)
    } else {
      removeInjectedElements()
    }
  }

  // onMounted: anchor divs (#communities-siteCustomHeader/Footer) are now in
  // the DOM — safe to inject header/footer HTML. CSS/JS go to <head> and would
  // work earlier, but we do everything here for consistency.
  onMounted(() => {
    applyIfChanged(effectiveCustomCode.value)
  })

  // Group customCode, preview query, or navigation away from group context.
  watch(
    () => [effectiveCustomCode.value, route.query.customCode, route.path, route.params.groupSlug] as const,
    () => {
      applyIfChanged(effectiveCustomCode.value)
    }
  )

  onUnmounted(() => {
    removeInjectedElements()
  })
}
